import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Collapse,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import useGalleryStore from '../../../store/galleryStore';
import { fetchCategories, GalleryCategory } from '../api/galleryApi';
import brand from '../../../config/brand';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'top_rated', label: 'Top Rated' },
];

const GalleryFilterSidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { filters, setFilters, resetFilters } = useGalleryStore();
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [expanded, setExpanded] = useState(!isMobile);
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    fetchCategories()
      .then(setCategories)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters({ search: searchInput });
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const content = (
    <Box>
      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search gallery..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: searchInput ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setSearchInput('');
                  setFilters({ search: '' });
                }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': { borderRadius: 2 },
        }}
      />

      {/* Sort */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}
        >
          Sort By
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={filters.sort}
            onChange={(e) => setFilters({ sort: e.target.value })}
            sx={{ borderRadius: 2 }}
          >
            {SORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Categories */}
      {categories.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}
          >
            Categories
          </Typography>
          <FormGroup>
            {categories.map((cat) => (
              <FormControlLabel
                key={cat.slug}
                control={
                  <Checkbox
                    checked={filters.category === cat.slug}
                    onChange={() =>
                      setFilters({
                        category:
                          filters.category === cat.slug ? '' : cat.slug,
                      })
                    }
                    size="small"
                    sx={{
                      '&.Mui-checked': { color: brand.colors.fire },
                    }}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2">{cat.name}</Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ ml: 1 }}
                    >
                      ({cat.count})
                    </Typography>
                  </Box>
                }
                sx={{
                  mx: 0,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              />
            ))}
          </FormGroup>
        </Box>
      )}

      {/* Clear Filters */}
      <Button
        fullWidth
        variant="outlined"
        size="small"
        startIcon={<ClearIcon />}
        onClick={() => {
          resetFilters();
          setSearchInput('');
        }}
        sx={{
          borderRadius: 2,
          borderColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.15)'
              : 'rgba(0,0,0,0.12)',
          color: 'text.secondary',
          '&:hover': {
            borderColor: brand.colors.fire,
            color: brand.colors.fire,
          },
        }}
      >
        Clear Filters
      </Button>
    </Box>
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 3,
          background:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(0,0,0,0.02)',
          borderRadius: 3,
          border: '1px solid',
          borderColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.06)'
              : 'rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        <Button
          fullWidth
          onClick={() => setExpanded(!expanded)}
          startIcon={<FilterListIcon />}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            py: 1.5,
            justifyContent: 'space-between',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          Filters
        </Button>
        <Collapse in={expanded}>
          <Box sx={{ px: 2, pb: 2 }}>{content}</Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.06)',
        borderRadius: 3,
        position: 'sticky',
        top: 80,
      }}
    >
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FilterListIcon sx={{ color: brand.colors.fire }} />
        Filters
      </Typography>
      {content}
    </Box>
  );
};

export default GalleryFilterSidebar;
